import { useQuery } from '@tanstack/react-query'
import type {
  IQuestionBankEntryDetail,
  IQuestionBankListResponse,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'

export const questionBankKeys = {
  all: ['question-bank'] as const,
  list: () => [...questionBankKeys.all, 'list'] as const,
  detail: (id: string) => [...questionBankKeys.all, 'detail', id] as const,
}

export async function fetchQuestionBankEntries(
  signal?: AbortSignal,
): Promise<IQuestionBankListResponse> {
  const { data: envelope } = await apiClient.get<ApiResponse<IQuestionBankListResponse>>(
    '/api/v1/question-bank',
    { signal },
  )
  if (!envelope?.data) {
    throw new Error('Question bank response missing data')
  }
  return envelope.data
}

export async function fetchQuestionBankEntryById(
  id: string,
  signal?: AbortSignal,
): Promise<IQuestionBankEntryDetail> {
  const { data: envelope } = await apiClient.get<ApiResponse<IQuestionBankEntryDetail>>(
    `/api/v1/question-bank/${encodeURIComponent(id)}`,
    { signal },
  )
  if (!envelope?.data?.id) {
    throw new Error('Question bank entry response missing data')
  }
  return envelope.data
}

export function useQuestionBankEntries() {
  return useQuery({
    queryKey: questionBankKeys.list(),
    queryFn: ({ signal }) => fetchQuestionBankEntries(signal),
  })
}

export function useQuestionBankEntryDetail(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: questionBankKeys.detail(id ?? ''),
    queryFn: ({ signal }) => fetchQuestionBankEntryById(id!, signal),
    enabled: enabled && Boolean(id),
  })
}
